import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  Label,
  Input,
  FieldError,
  AutocompleteWrapper,
  AutocompleteDropdown,
  SuggestionItem,
  SuggestionCusip,
  SuggestionSecurity,
} from './TicketStyledComponents';

/**
 * Reference-data-driven security picker for securitized / structured tickets.
 *
 * Instead of defining a deal/tranche/facility inline on the ticket, the trader
 * searches the Security Master for an instrument of the given asset class (the
 * deal+tranche having been defined once in Reference -> Structured Products).
 * Selecting one returns its securityId + cusip for the thin transaction payload.
 *
 * When nothing matches, the picker surfaces a "Define new instrument" action that
 * deep-links to the Reference Data workspace (pre-filtered to this asset class).
 *
 * Mirrors the CUSIP autocomplete in FixedIncomeTicketScreen, but scoped to one
 * asset class via GET /api/dimensions/securities/search-by-class.
 */

export interface PickedSecurity {
  id: number;
  cusip?: string;
  securityName?: string;
  currency?: string;
  faceValue?: number;
  maturityDate?: string;
  [key: string]: any;
}

interface SecurityPickerProps {
  /** Asset class code to scope the search, e.g. "RESIDENTIAL_MBS". */
  assetClass: string;
  /** Currently selected security (controlled by the parent ticket). */
  selected?: PickedSecurity | null;
  onSelect: (security: PickedSecurity) => void;
  onClear?: () => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  /** Label for the deep-link shown when no instrument matches. */
  notFoundLabel?: string;
  /** Invoked when the user clicks the "define new instrument" deep-link. */
  onDefineNew?: () => void;
}

const SelectedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(96, 165, 250, 0.4);
  background: rgba(59, 130, 246, 0.1);
  border-radius: 12px;
`;

const SelectedMeta = styled.div`
  min-width: 0;
`;

const SelectedCusip = styled.div`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 700;
  color: #60a5fa;
  font-size: 13px;
  letter-spacing: 0.02em;
`;

const SelectedName = styled.div`
  font-size: 12px;
  color: #cbd5e1;
  margin-top: 3px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #60a5fa;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
  &:disabled { color: #64748b; cursor: not-allowed; }
`;

const EmptyRow = styled.div`
  padding: 14px 16px;
  font-size: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SecurityPicker: React.FC<SecurityPickerProps> = ({
  assetClass,
  selected,
  onSelect,
  onClear,
  label = 'Instrument (CUSIP)',
  placeholder = 'Search by deal, tranche, or CUSIP…',
  error,
  disabled,
  notFoundLabel = 'Define new instrument',
  onDefineNew,
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PickedSecurity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = async (q: string) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/dimensions/securities/search-by-class', {
        params: { q: q || '', assetClass, limit: 20 },
      });
      setSuggestions(Array.isArray(response.data) ? response.data : []);
      setOpen(true);
    } catch (err) {
      console.error('Security search failed:', err);
      setSuggestions([]);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => { if (blurTimer.current) clearTimeout(blurTimer.current); };
  }, []);

  const handleSelect = (security: PickedSecurity) => {
    setOpen(false);
    setSuggestions([]);
    setQuery('');
    onSelect(security);
  };

  if (selected && selected.id) {
    return (
      <div>
        <Label>{label}</Label>
        <SelectedCard>
          <SelectedMeta>
            <SelectedCusip>{selected.cusip || `#${selected.id}`}</SelectedCusip>
            <SelectedName>{selected.securityName || '—'}</SelectedName>
          </SelectedMeta>
          {!disabled && onClear && (
            <LinkButton type="button" onClick={onClear}>Change</LinkButton>
          )}
        </SelectedCard>
      </div>
    );
  }

  return (
    <div>
      <Label>{label}</Label>
      <AutocompleteWrapper>
        <Input
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          className={error ? 'error' : ''}
          onChange={(e) => {
            setQuery(e.target.value);
            fetchSuggestions(e.target.value.trim());
          }}
          onFocus={() => fetchSuggestions(query.trim())}
          onBlur={() => {
            // Delay so a click on a suggestion registers first.
            blurTimer.current = setTimeout(() => setOpen(false), 200);
          }}
        />
        {open && (
          <AutocompleteDropdown>
            {suggestions.length > 0 ? (
              suggestions.map((s) => (
                <SuggestionItem key={s.id} onClick={() => handleSelect(s)}>
                  <SuggestionCusip>{s.cusip || `#${s.id}`}</SuggestionCusip>
                  <SuggestionSecurity>{s.securityName || '—'}</SuggestionSecurity>
                </SuggestionItem>
              ))
            ) : (
              <EmptyRow>
                <span>{loading ? 'Searching…' : 'No matching instruments'}</span>
                {!loading && onDefineNew && (
                  <LinkButton type="button" onClick={onDefineNew}>
                    {notFoundLabel} →
                  </LinkButton>
                )}
              </EmptyRow>
            )}
          </AutocompleteDropdown>
        )}
      </AutocompleteWrapper>
      {error && <FieldError>{error}</FieldError>}
      {onDefineNew && (
        <div style={{ marginTop: 6 }}>
          <LinkButton type="button" onClick={onDefineNew} disabled={disabled}>
            + {notFoundLabel} in Reference Data
          </LinkButton>
        </div>
      )}
    </div>
  );
};

export default SecurityPicker;
